import React from 'react';
import PropTypes from 'prop-types';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  Box,
  Button,
  Divider,
  Grid,
  ListItem,
  ListItemIcon,
  ListItemText,
  Slide,
  Tab,
  Tabs,
  Typography,
  ListItemButton,
  Avatar,
  List,
  IconButton,
  ListItemAvatar,
  Chip,
} from '@mui/material';

// actions
import {
  AccessAlarm,
  AccountBox,
  AttachFile,
  Group,
  History,
  Info,
  LocalOffer,
  LocationOn,
  PendingActions,
  Bookmark,
  CheckBox,
  CheckBoxOutlineBlank,
  Delete,
} from '@mui/icons-material';

import Property from '../../Property';
import EventUpload from '../../EventUpload';
import Horse, { getHorsePictureUrl } from '../../Horse';
import LocationChip from '../../LocationChip';
import HorseIcon from '../../CustomIcons/Horse';
import User from '../../User';
import { formatDateString } from '../../../utils/dateHelper';
import TagList from '../../TagList';
import { EditTaskEventDataComponent } from '../EditTaskEventData';
import EventHistoryCard from '../EventHistoryCard';
import AddCommentCard from '../AddCommentCard';
import { getIconForItem } from '../../../utils/iconHelper';
import TaskStatus, { getColorForItem } from '../../TaskStatus';
import { grey } from '../../../utils/brandColors';
import { getBarnHorse } from '../../Schedule/ScheduleTaskItem';
import { getLocationForTags } from '../../../utils/tagsHelper';
import { setSelectedTaskForSchedule } from '../../../redux/app/actions';
import { getIcon } from '../../../pages/horse/Horse/components/LocationsCard';

export const transition = React.forwardRef((props, ref) => <Slide direction="up" {...props} ref={ref} />);

const getIconForTask = (task, selectedBarn) => {
  if (task.archived) {
    return (
      <Delete
        sx={{
          width: '16px',
          height: '16px',
        }}
        viewBox="3 3 18 18"
      />
    )
  }
  if (task.currentEventStatus && task.currentEventStatus.status.id !== selectedBarn.barnMetadata.statusPendingId
    && task.currentEventStatus.status.id !== selectedBarn.barnMetadata.statusStartId
    && task.currentEventStatus.status.id !== selectedBarn.barnMetadata.statusEndId) {
    return (
      <Bookmark
        sx={{
          width: '16px',
          height: '16px',
        }}
        viewBox="3 3 18 18"
        color="primary"
      />
    )
  }
  if (!task.archived && task.completeDate) {
    return (
      <CheckBox
        sx={{
          width: '16px',
          height: '16px',
        }}
        viewBox="3 3 18 18"
        color="primary"
      />
    )
  }
  if (!task.archived && !task.completeDate) {
    return (
      <CheckBoxOutlineBlank
        sx={{
          width: '16px',
          height: '16px',
        }}
        viewBox="3 3 18 18"
        color="primary"
      />
    )
  }
  return (
    <Bookmark
      sx={{
        width: '16px',
        height: '16px',
      }}
      viewBox="3 3 18 18"
      color="primary"
    />
  )
}

const TaskHighlightComponent = ({
  task,
  currentStatusAssignment,
  detailedLocation,
  scheduleAssignment,
  canAttachFiles,
  handleClose,
  handleEventStatus,
  isUpdatingEvent,
  selectedEventBarnHorse,
  selectedEventLocation,
  onEdit,
  associatedTasks,
}) => {
  const dispatch = useDispatch();

  const [tab, setTab] = React.useState(0);
  const [actionType, setActionType] = React.useState('Comment')
  React.useEffect(() => {
    setTab(0);
    setActionType('Comment')
  }, [task])
  const barnUsers = useSelector((state) => state.barn.barnUsers);
  const statuses = useSelector((state) => state.status.statuses);
  const barnHorsesMap = useSelector((state) => state.barn.barnHorsesMap);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const locationsMap = useSelector((state) => state.location.locationsMap)

  const editProps = {
    sx: { '&:hover': { cursor: 'pointer' } },
    onClick: onEdit,
  }
  const headerBackgroundColor = (task && task.eventData && task.eventData.Color && task.eventData.Color.value)
    || task.main;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
        }}
      >
        <Tabs
          value={tab}
          onChange={(e, t) => setTab(t)}
          aria-label="Task Tabs"
          sx={{
            minHeight: 35,
            maxHeight: 35,
          }}
        >
          <Tab
            sx={{
              py: 0,
              minHeight: 28,
              maxHeight: 28,
            }}
            icon={<Info sx={{ mr: '4px !important' }} />}
            iconPosition="start"
            label="Details"
          />
          <Tab
            sx={{
              py: 0,
              minHeight: 28,
              maxHeight: 28,
            }}
            icon={<History sx={{ mr: '4px !important' }} />}
            iconPosition="start"
            label={`History (${(task.eventStatuses || []).length + (task.eventUploads || []).length + (task.comments || []).filter((comment) => comment.commentHeader.indexOf('New Status For Event') === -1).length})`}
          />
          <Tab
            sx={{
              py: 0,
              minHeight: 28,
              maxHeight: 28,
              display: 'none',
            }}
            icon={<PendingActions sx={{ mr: '4px !important' }} />}
            iconPosition="start"
            label="Actions"
          />
        </Tabs>

        <ListItem
          dense
          sx={{
            pl: 1,
            mt: 1,
          }}
        >
          <ListItemText
            sx={{
              backgroundColor: grey[50],
              borderRadius: 4,
              mt: 0,
              mb: 1,
            }}
          >
            <TaskStatus
              eventStatus={task.currentEventStatus}
              icon={getIconForItem(statuses, task.currentEventStatus && task.currentEventStatus.status.id)}
              color={getColorForItem(statuses, task.currentEventStatus && task.currentEventStatus.status.id)}
              archived={task.archived}
              complete={!!(task && task.completeDate)}
            />
          </ListItemText>
        </ListItem>

        <ListItem dense>
          <ListItemIcon>
            {selectedEventBarnHorse
              ? <HorseIcon />
              : <LocationOn />}
          </ListItemIcon>
          <ListItemText>
            {selectedEventBarnHorse && (<Horse horse={selectedEventBarnHorse.horse} />)}
            {selectedEventLocation && (<LocationChip location={selectedEventLocation} />)}
          </ListItemText>

        </ListItem>
        {
          tab === 0 && (
            <Grid container>
              <Grid item xs={12}>
                <ListItem dense>
                  <ListItemIcon><LocalOffer /></ListItemIcon>
                  <ListItemText {...editProps}>
                    <TagList
                      disableGutters
                      tags={task.tags.filter((t) => t.id !== ((selectedEventBarnHorse && parseInt(selectedEventBarnHorse.pingumTagId, 10))
                        || (selectedEventLocation && parseInt(selectedEventLocation.pingumTagId, 10)) || -1))}
                    />
                  </ListItemText>
                </ListItem>
              </Grid>
              <Grid item xs={12}>
                <ListItem dense>
                  <ListItemIcon><AccountBox /></ListItemIcon>
                  <ListItemText {...editProps}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ fontWeight: 300 }}>{currentStatusAssignment
                        ? 'Assigned to'
                        : 'No Task Assignment'}
                      </Box>
                      {currentStatusAssignment
                        && (
                          currentStatusAssignment.split(/(?=@\[)/)
                            .map((user) => (
                              <User
                                key={user.auth0UserId}
                                chip
                                user={{
                                  auth0UserId: user.match(/@\[.*\]\((.*)\)/)[1],
                                  userFullName: (user && user.substr(2, user.indexOf('](') - 2)) || '',
                                }}
                                sx={{ margin: '0 0 0 8px !important' }}
                              />
                            ))
                        )}
                    </Box>
                  </ListItemText>
                </ListItem>
              </Grid>
              <Grid item xs={12}>
                <ListItem dense>
                  <ListItemIcon><Group /></ListItemIcon>
                  <ListItemText {...editProps}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ fontWeight: 300 }}>{scheduleAssignment
                        ? 'Participants'
                        : 'No Particpants'}
                      </Box>
                      {scheduleAssignment
                        && (
                          scheduleAssignment.split(/(?=@\[)/)
                            .map((user) => (
                              <User
                                key={user.auth0UserId}
                                chip
                                user={{
                                  auth0UserId: user.match(/@\[.*\]\((.*)\)/)[1],
                                  userFullName: (user && user.substr(2, user.indexOf('](') - 2)) || '',
                                }}
                                sx={{ margin: '0 0 0 8px !important' }}
                              />
                            ))
                        )}
                    </Box>
                  </ListItemText>
                </ListItem>
              </Grid>
              {
                !task.completeDate && task.dueDate && (
                  <Grid item xs={12}>
                    <ListItem dense>
                      <ListItemIcon><AccessAlarm /> </ListItemIcon>
                      <ListItemText {...editProps}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {task.dueDate && <Box>Complete task by</Box>}
                          <Box
                            sx={{
                              ml: task.dueDate
                                ? '4px'
                                : 0,
                              color: (!task.completeDate && task.dueDate && new Date(task.dueDate).getTime() < new Date().getTime())
                                ? 'red'
                                : 'inherit',
                            }}
                          >
                            {task.dueDate
                              ? `${formatDateString(task.dueDate)}`
                              : 'No reminders for this task'}
                          </Box>
                        </Box>
                      </ListItemText>
                    </ListItem>
                  </Grid>
                )
              }
              <Grid item xs={12}>
                <ListItem dense>
                  <ListItemIcon><Info /></ListItemIcon>
                  <ListItemText {...editProps}>
                    {task.description || <Box sx={{ fontWeight: 300 }}>No task information</Box>}
                  </ListItemText>
                </ListItem>
              </Grid>
              <Grid item xs={12}>
                <ListItem dense>
                  <ListItemIcon><LocationOn /></ListItemIcon>
                  <ListItemText {...editProps}>
                    {detailedLocation || <Box sx={{ fontWeight: 300 }}>No location</Box>}
                  </ListItemText>
                </ListItem>
              </Grid>
              {
                selectedEventBarnHorse && (
                  <Grid item xs={12} {...editProps}>
                    <EditTaskEventDataComponent
                      taskEventData={task.eventData}
                      onChange={null}
                      completeDate={task.completeDate}
                      selectedEvent={task}
                      barnHorse={selectedEventBarnHorse}
                      showDivider={false}
                    />
                  </Grid>
                )
              }
              <Grid item xs={12}>
                {
                  canAttachFiles
                  && task.eventUploads
                  && task.eventUploads.length > 0
                  && (
                    <ListItem dense>
                      <ListItemIcon><AttachFile /></ListItemIcon>
                      <ListItemText>
                        <Property disableGutters label="Task Attachments">
                          {
                            task.eventUploads.map((upload) => (
                              <div
                                key={upload.id}
                              >
                                <EventUpload eventUpload={upload} editable />
                              </div>
                            ))
                          }
                        </Property>
                      </ListItemText>
                    </ListItem>
                  )
                }
              </Grid>

              {
                associatedTasks.length > 0 && (
                  <Grid
                    item
                    xs={12}
                  >
                    <Divider sx={{ mb: 1 }} />
                    {
                      associatedTasks.length > 0 && <Typography variant="captions" sx={{ fontWeight: 300 }}>All Related Tasks</Typography>
                    }
                    {
                      associatedTasks.length > 0 && (
                        <List
                          style={{
                            padding: 0,
                            overflowY: 'auto',
                          }}
                        >
                          {
                            associatedTasks.map((t) => {
                              const barnHorse = getBarnHorse(t, null, barnHorsesMap, selectedBarn.barnMetadata.horseTagTypeId);
                              const location = !barnHorse && getLocationForTags(t.tags, selectedBarn.barnMetadata.locationTagTypeId, locationsMap);
                              return (
                                <ListItemButton
                                  key={t.id}
                                  dense
                                  disablePadding
                                  disabled={t.archived}
                                  onClick={() => { dispatch(setSelectedTaskForSchedule(t)); }}
                                  sx={{
                                    borderLeft: `6px solid ${headerBackgroundColor}`,
                                    background: t.completeDate
                                      ? 'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAAXNSR0IArs4c6QAAAEFJREFUKFNjZCAAzp492wBSwohPHUyRsbFxA06FyIpwmoiuCKtCbIowFOJShKIQnyK4QkKKwAqJUQRXCAonQgEPAL3PJvK6J0w6AAAAAElFTkSuQmCC)'
                                      : undefined,
                                    backgroundColor: t.completeDate
                                      ? 'rgb(182, 190, 196)'
                                      : undefined,
                                  }}
                                >
                                  <ListItemAvatar>
                                    <Avatar
                                      alt={barnHorse
                                        ? barnHorse.horse.name
                                        : ((location && location.name) || 'none')}
                                      src={barnHorse
                                        ? getHorsePictureUrl(barnHorse.horse)
                                        : null}
                                    >{location
                                      ? getIcon(location)
                                      : ''}
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText
                                    sx={{ flex: 1 }}
                                    primary={barnHorse
                                      ? barnHorse.horse.name
                                      : location.name}
                                    secondary={barnHorse && barnHorse.horse.showName}
                                  />
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    {task.id === t.id && <Chip size="small" label="Selected" color="primary" />}
                                    <IconButton
                                      edge="end"
                                      aria-label="delete"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEventStatus({ completeEvent: !t.completeDate }, t, true)
                                      }}
                                    >
                                      {getIconForTask(t, selectedBarn)}
                                    </IconButton>
                                  </Box>
                                </ListItemButton>

                              )
                            })
                          }
                        </List>
                      )
                    }
                  </Grid>
                )
              }
            </Grid>
          )
        }
        {
          tab === 1 && (
            <EventHistoryCard showActionCard={false} tenantUsers={barnUsers} />
          )
        }
        {
          tab === 2 && (
            <AddCommentCard onSuccessfulAction={() => { setTab(1); }} actionType={actionType} />
          )
        }
      </Box>
      <Box>
        <Divider sx={{ pb: 2 }} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button sx={{ mr: 1 }} onClick={handleClose}>Cancel</Button>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              py: 1,
              flex: 1,
            }}
          >
            {
              tab !== 2
                ? (
                  <Button sx={{ mr: 1 }} color="secondary" variant="outlined" onClick={() => { setActionType('status'); setTab(2) }}>
                    Change Status
                  </Button>
                )
                : <Box />
            }
            {
              tab !== 2 && (
                <Button
                  sx={{ flex: 1 }}
                  onClick={() => handleEventStatus({ completeEvent: !task.completeDate })}
                  disabled={isUpdatingEvent}
                  color="primary"
                  variant={task.completeDate
                    ? 'text'
                    : 'contained'}
                >
                  {
                    task.completeDate
                      ? 'Mark Uncomplete'
                      : 'Mark Complete'
                  }
                </Button>
              )
            }
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

TaskHighlightComponent.propTypes = {
  task: PropTypes.object.isRequired,
  canAttachFiles: PropTypes.bool,
  currentStatusAssignment: PropTypes.string,
  detailedLocation: PropTypes.string,
  scheduleAssignment: PropTypes.string,
  handleClose: PropTypes.func.isRequired,
  handleEventStatus: PropTypes.func.isRequired,
  isUpdatingEvent: PropTypes.bool,
  selectedEventBarnHorse: PropTypes.object,
  selectedEventLocation: PropTypes.object,
  onEdit: PropTypes.func.isRequired,
  associatedTasks: PropTypes.array,
};

TaskHighlightComponent.defaultProps = {
  canAttachFiles: false,
  currentStatusAssignment: null,
  detailedLocation: null,
  scheduleAssignment: null,
  isUpdatingEvent: false,
  selectedEventBarnHorse: null,
  selectedEventLocation: null,
  associatedTasks: [],
};

export default (TaskHighlightComponent);
